import { NextSeo } from "next-seo";

import JsonLd from "@/components/seo/JsonLd";
import { authorHref } from "@/components/blog/PostMeta";
import { BLOG_DEFAULT_IMAGE, absoluteBlogUrl, postPath, topicPath } from "@/lib/blog/paths";
import { topicForPost } from "@/lib/blog/topics";
import { FOUNDERS } from "@/lib/founders/content";
import type { BlogPost } from "@/lib/ghost";
import { blogPosting, breadcrumbList, person, type BreadcrumbItem } from "@/lib/seo/schema";

const FOUNDER_PERSON_IDS: Record<string, string | undefined> = {
  aleem: FOUNDERS.find((founder) => founder.slug === "aleem-rehmtulla")?.personId,
};

const clamp = (text: string, max = 160) => {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 3);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:]$/, "")}...`;
};

export const blogPostBreadcrumbs = (post: BlogPost): BreadcrumbItem[] => {
  const topic = topicForPost(post);
  return [
    { name: "Home", href: "/" },
    { name: "Blog", href: "/blog" },
    ...(topic ? [{ name: topic.name, href: topicPath(topic.slug) }] : []),
    { name: post.title, href: postPath(post.slug) },
  ];
};

export const BlogSeo = ({ post }: { post: BlogPost }) => {
  const topic = topicForPost(post);
  const url = absoluteBlogUrl(postPath(post.slug));
  const description = clamp(post.meta_description || post.excerpt);
  const image = post.feature_image || BLOG_DEFAULT_IMAGE;
  const author = post.primary_author;
  const authorUrl = author ? authorHref(author.slug) : "/blog";

  return (
    <>
      <NextSeo
        title={post.title}
        titleTemplate="%s | Crayo Blog"
        description={description}
        canonical={url}
        openGraph={{
          type: "article",
          url,
          title: post.title,
          description,
          siteName: "Crayo",
          locale: "en_US",
          article: {
            publishedTime: post.published_at,
            modifiedTime: post.updated_at,
            authors: [absoluteBlogUrl(authorUrl)],
            section: topic?.name,
            tags: post.tags.map((tag) => tag.name),
          },
          images: [{ url: image, alt: post.feature_image_alt || post.title }],
        }}
        twitter={{ cardType: "summary_large_image", handle: "@Crayoai", site: "@Crayoai" }}
        robotsProps={{ maxImagePreview: "large" }}
      />
      <JsonLd
        nodes={[
          blogPosting({
            url,
            headline: post.title,
            description,
            image,
            datePublished: post.published_at,
            dateModified: post.updated_at,
            author: person({
              name: author?.name ?? "Crayo",
              url: authorUrl,
              id: author ? FOUNDER_PERSON_IDS[author.slug] : undefined,
            }),
            section: topic?.name,
            keywords: post.tags.map((tag) => tag.name),
            timeRequired: post.reading_time > 0 ? `PT${post.reading_time}M` : undefined,
          }),
          breadcrumbList(blogPostBreadcrumbs(post)),
        ]}
      />
    </>
  );
};
